import React from 'react';
import { FaTooth, FaSmile, FaStar, FaHeart, FaShieldAlt, FaUserMd } from 'react-icons/fa';

const PediatricDentistry = () => {
  const services = [
    {
      icon: <FaTooth className="w-12 h-12 text-blue-400" />,
      title: "First Dental Visit",
      description: "A gentle introduction to dental care for your little one's first visit",
      age: "Ages 1-3",
      features: [
        "Fun, no-pressure environment",
        "Gentle examination",
        "Parent education",
        "Free toothbrush and stickers"
      ]
    },
    {
      icon: <FaSmile className="w-12 h-12 text-pink-400" />,
      title: "Preventive Care",
      description: "Keeping those tiny teeth healthy and strong",
      age: "All Ages",
      features: [
        "Regular check-ups",
        "Dental cleanings",
        "Fluoride treatments",
        "Sealants"
      ]
    },
    {
      icon: <FaStar className="w-12 h-12 text-yellow-400" />,
      title: "Cavity Care",
      description: "Making cavity treatment comfortable and stress-free",
      age: "All Ages",
      features: [
        "Gentle numbing",
        "Colorful fillings",
        "Quick procedures",
        "Reward system"
      ]
    }
  ];

  const features = [
    {
      icon: <FaHeart className="w-8 h-8 text-red-400" />,
      title: "Child-Friendly Environment",
      description: "Our office is designed to make kids feel at home with toys, games, and a fun atmosphere"
    },
    {
      icon: <FaUserMd className="w-8 h-8 text-green-400" />,
      title: "Specialized Pediatric Team",
      description: "Our team is specially trained to work with children and make them feel comfortable"
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-purple-400" />,
      title: "Safe & Modern Equipment",
      description: "We use the latest technology to ensure safe and comfortable treatments"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-blue-500 text-white py-20">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-90"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1516627145497-ae6968895b74?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.1
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Making Dental Visits Fun! 🦷
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl">
            Where every child leaves with a smile. We make dental care exciting and comfortable for your little ones.
          </p>
        </div>
      </div>

      {/* Services Section */}
      <div className="container mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">
          Our Kid-Friendly Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-transform duration-300">
              <div className="mb-6">{service.icon}</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">{service.title}</h3>
              <p className="text-blue-500 font-semibold mb-4">{service.age}</p>
              <p className="text-gray-600 mb-6">{service.description}</p>
              <ul className="space-y-3">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-gray-700">
                    <span className="text-green-500 mr-2">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 py-16">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">
            Why Kids Love Us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="flex justify-center mb-6">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready for a Fun Dental Visit?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Schedule your child's appointment today and let's make dental care exciting!
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="/schedule-appointment"
              className="inline-block bg-white text-blue-500 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
            >
              Schedule Visit
            </a>
            <a
              href="/virtual-tour"
              className="inline-block bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-500 transition-colors"
            >
              Take Virtual Tour
            </a>
          </div>
        </div>
      </div>

      {/* Fun Facts Section */}
      <div className="container mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            Fun Dental Facts for Kids! 🌟
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="flex items-start">
                <span className="text-2xl mr-3">🦷</span>
                <p className="text-gray-700">Did you know? Your teeth are as unique as your fingerprints!</p>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-3">🌟</span>
                <p className="text-gray-700">The first tooth usually appears when you're about 6 months old!</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start">
                <span className="text-2xl mr-3">💫</span>
                <p className="text-gray-700">Brushing your teeth for 2 minutes is like watching a short cartoon!</p>
              </div>
              <div className="flex items-start">
                <span className="text-2xl mr-3">🌈</span>
                <p className="text-gray-700">Your mouth makes enough saliva to fill two swimming pools in your lifetime!</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PediatricDentistry; 